import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient, PLATFORM, PLAYGROUND } from '../services/apiClient';
import { Auth, cookieUtils } from '../utils';
import { getUserFromToken, setToken } from '../services/user';
import GoogleLogin from './GoogleLogin';

type CostEntry = { inputCost?: number; outputCost?: number; totalCost?: number };
type CostDoc = {
    scanGradeId: string;
    scanGradeHomeworkId?: string;
    costs?: Record<string, CostEntry> & { totalCost?: number };
    tokens?: Record<string, any>;
};

// Loose typing — the `scanGrades` collection has many fields and varies by
// grading_mode. We render whatever's present without forcing a schema.
type AnalysisDoc = Record<string, any>;

type QuestionDoc = {
    _id?: string;
    questionImageUrl?: string;
    specification?: string;
    questionType?: string;
    parts?: any[];
    marks?: number;
};

type Response = {
    costDoc: CostDoc | null;
    analysis: AnalysisDoc | null;
    question: QuestionDoc | null;
};

const STORAGE_BUCKET_NAME = 'diraq-contents';

const formatCost = (n?: number) => `$${(n ?? 0).toFixed(6)}`;

// Pull a `<bucket>/<path-inside-bucket>` filename out of a GCS signed URL so we
// can ask platform-node to re-sign it when the original URL expires.
const extractFileNameFromUrl = (url: string): string | null => {
    if (!url) return null;
    try {
        const trimmed = url.split('?')[0];
        const marker = `${STORAGE_BUCKET_NAME}/`;
        const idx = trimmed.indexOf(marker);
        if (idx === -1) return null;
        const fileName = trimmed.slice(idx + marker.length);
        return fileName ? decodeURIComponent(fileName) : null;
    } catch (_) {
        return null;
    }
};

const getFreshDownloadUrl = async (fileName: string, token: string): Promise<string | null> => {
    if (!fileName || !token) return null;
    try {
        const resp = await apiClient.post<{ data?: { downloadUrl?: string } }>(
            '/file/download-url',
            PLATFORM,
            { fileName, bucketName: STORAGE_BUCKET_NAME }
        );
        return resp?.data?.downloadUrl || null;
    } catch (err) {
        console.warn('[ScanGradeQuestionPage] failed to re-sign', fileName, err);
        return null;
    }
};

// Pull image URLs out of the analysis doc no matter which field the writer used.
const collectImageUrls = (analysis: AnalysisDoc | null | undefined, key: string): string[] => {
    if (!analysis) return [];
    const direct = analysis[key];
    if (Array.isArray(direct)) return direct.filter((s) => typeof s === 'string');
    if (typeof direct === 'string') return [direct];
    return [];
};

const ScanGradeQuestionPage: React.FC = () => {
    const { scanGradeId } = useParams<{ scanGradeId: string }>();
    const [data, setData] = useState<Response | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<any | null>(null);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [refreshedUrls, setRefreshedUrls] = useState<Record<string, string>>({});
    const [refreshing, setRefreshing] = useState(false);
    const [refreshStatus, setRefreshStatus] = useState<{ message: string; tone: 'success' | 'error' | 'neutral' } | null>(null);

    const googleLoginUrl = `${process.env.REACT_APP_PLATFORM_URL}/auth/google?redirect=${window.location.href}`;
    const token = cookieUtils.get(Auth.TOKEN);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const urlToken = searchParams.get('token');
        if (urlToken) setToken(urlToken);
    }, []);

    useEffect(() => {
        getUserFromToken()
            .then((u) => setUser(u && u.id ? u : null))
            .catch(() => setUser(null))
            .finally(() => setIsCheckingAuth(false));
    }, [token]);

    useEffect(() => {
        if (!scanGradeId) {
            setError('No scanGradeId provided');
            setLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const resp = await apiClient.get<Response>(
                    `/api/scan-grade/question/${scanGradeId}`,
                    PLAYGROUND
                );
                if (!cancelled) {
                    setData(resp);
                    setRefreshedUrls({}); // Clear stale re-signed URLs when data reloads
                }
            } catch (err: any) {
                if (!cancelled) setError(err?.message || 'Failed to load question');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [scanGradeId]);

    const { costDoc, analysis, question } = data || {};
    const costs = costDoc?.costs || {};
    const hwId = costDoc?.scanGradeHomeworkId;

    // The scanGrades doc uses `solutionUrl` (singular field, array value).
    // Older writers may use `solutionUrls` / `questionImages`; collect all.
    const rawSolutionUrls = useMemo(() => {
        if (!analysis) return [] as string[];
        const fromAnalysis = [
            ...collectImageUrls(analysis, 'solutionUrl'),
            ...collectImageUrls(analysis, 'solutionUrls'),
            ...collectImageUrls(analysis, 'studentSolutionUrls'),
        ];
        return Array.from(new Set(fromAnalysis));
    }, [analysis]);
    // Question images: scanGrades doc rarely has one, so we mainly rely on
    // the resolved `question` doc (questions.questionImageUrl). Still fall
    // back to any analysis.questionUrl* variants if a writer set them.
    const rawQuestionUrls = useMemo(() => {
        const urls: string[] = [];
        if (question?.questionImageUrl) urls.push(question.questionImageUrl);
        if (analysis) {
            urls.push(
                ...collectImageUrls(analysis, 'questionUrl'),
                ...collectImageUrls(analysis, 'questionUrls'),
                ...collectImageUrls(analysis, 'questionImages'),
            );
        }
        return Array.from(new Set(urls));
    }, [analysis, question]);

    const solutionUrls = rawSolutionUrls.map((u) => refreshedUrls[u] || u);
    const questionUrls = rawQuestionUrls.map((u) => refreshedUrls[u] || u);

    const handleRefreshImages = useCallback(async () => {
        const authToken = cookieUtils.get(Auth.TOKEN) || '';
        if (!authToken) {
            setRefreshStatus({ message: 'Auth token missing; please log in again.', tone: 'error' });
            return;
        }
        const allUrls = [...rawQuestionUrls, ...rawSolutionUrls];
        if (allUrls.length === 0) {
            setRefreshStatus({ message: 'No images to refresh.', tone: 'neutral' });
            return;
        }

        setRefreshing(true);
        setRefreshStatus({ message: 'Refreshing images...', tone: 'neutral' });

        const unique = Array.from(new Set(allUrls));
        const updates: Record<string, string> = {};
        let failures = 0;

        await Promise.all(unique.map(async (original) => {
            const fileName = extractFileNameFromUrl(original);
            if (!fileName) {
                failures += 1;
                return;
            }
            const fresh = await getFreshDownloadUrl(fileName, authToken);
            if (fresh) {
                updates[original] = fresh;
            } else {
                failures += 1;
            }
        }));

        setRefreshedUrls((prev) => ({ ...prev, ...updates }));
        setRefreshing(false);
        if (Object.keys(updates).length === 0) {
            setRefreshStatus({ message: 'Failed to refresh any images.', tone: 'error' });
        } else if (failures > 0) {
            setRefreshStatus({ message: `Refreshed ${Object.keys(updates).length} of ${unique.length} images.`, tone: 'error' });
        } else {
            setRefreshStatus({ message: 'Images refreshed.', tone: 'success' });
        }
    }, [rawQuestionUrls, rawSolutionUrls]);

    if (isCheckingAuth) return <div style={{ padding: 50, textAlign: 'center' }}><h2>Checking authentication...</h2></div>;
    if (!user) {
        return (
            <div style={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GoogleLogin
                    loginUrl={googleLoginUrl}
                    onLoginClick={() => {}}
                    isOpen={true}
                    onClose={() => {}}
                    disabled={false}
                    title="Sign in to view question"
                    description="Please sign in to access scan-and-grade question details."
                />
            </div>
        );
    }
    if (loading) return <div style={{ padding: 50, textAlign: 'center' }}><h2>Loading question…</h2></div>;
    if (error) return <div style={{ padding: 50, textAlign: 'center', color: 'red' }}><h2>Error</h2><p>{error}</p></div>;
    if (!data || (!data.costDoc && !data.analysis)) {
        return <div style={{ padding: 50, textAlign: 'center' }}><h2>Not found</h2></div>;
    }

    const gradingEntries: [string, CostEntry][] = (Object.entries(costs)
        .filter(([k, v]) => k.startsWith('scanGradeGrading_') && typeof v === 'object' && v !== null)) as [string, CostEntry][];

    const teacherAnalysis = analysis?.teacherAnalysis || analysis?.TeacherAnalysis;
    const studentAnalysis = analysis?.studentAnalysis || analysis?.StudentAnalysis;
    const overallGrade = analysis?.overallGrade ?? analysis?.overall_grade;
    const status = analysis?.status;
    const defaultBreakdown = analysis?.defaultBreakdown;
    const markschemeBreakdown = analysis?.markschemeBreakdown;
    const conceptGrades: any[] = Array.isArray(analysis?.conceptGrades) ? (analysis as any).conceptGrades : [];

    return (
        <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            <div style={{ fontSize: 13 }}>
                {hwId && <Link to={`/scan-grade/homework/${hwId}`} style={{ color: '#3b82f6' }}>← Back to homework {hwId}</Link>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <h1 style={{ marginTop: 0, marginBottom: 4, fontSize: 22 }}>Scan &amp; Grade Question</h1>
                    <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#374151' }}>{scanGradeId}</div>
                </div>
                <button
                    onClick={handleRefreshImages}
                    disabled={refreshing || (rawQuestionUrls.length + rawSolutionUrls.length) === 0}
                    style={refreshBtn(refreshing)}
                >
                    {refreshing ? 'Refreshing…' : '🔄 Refresh images'}
                </button>
            </div>
            {refreshStatus && (
                <div style={refreshStatusBox(refreshStatus.tone)}>{refreshStatus.message}</div>
            )}

            <section style={section}>
                <h2 style={h2}>Question</h2>
                {question?.specification && (
                    <div style={{ ...feedbackBox, marginBottom: 12 }}>{question.specification}</div>
                )}
                {questionUrls.length > 0 ? (
                    <ImageGrid label="Question image" urls={questionUrls} />
                ) : (
                    <div style={{ color: '#6b7280', fontSize: 13 }}>
                        {question
                            ? 'No question image on the question document.'
                            : analysis?.questionId
                                ? `Question ${String(analysis.questionId).slice(-6)} not found in questions collection.`
                                : 'No questionId on this attempt.'}
                    </div>
                )}
            </section>

            <section style={section}>
                <h2 style={h2}>Student solution</h2>
                {solutionUrls.length > 0 ? (
                    <ImageGrid label="Solution image" urls={solutionUrls} />
                ) : (
                    <div style={{ color: '#6b7280', fontSize: 13 }}>No solution images attached.</div>
                )}
            </section>

            <section style={section}>
                <h2 style={h2}>Cost breakdown</h2>
                <table style={table}>
                    <thead>
                        <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                            <th style={th}>Stage</th>
                            <th style={th}>Input</th>
                            <th style={th}>Output</th>
                            <th style={th}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <Row label="Segmentation (apportioned)" entry={costs.scanGradeSegmentation} />
                        {gradingEntries.map(([key, val]) => (
                            <Row key={key} label={`Grading (${key.replace('scanGradeGrading_', '')})`} entry={val} />
                        ))}
                        <tr style={{ borderTop: '2px solid #111827', fontWeight: 600 }}>
                            <td style={td}>Total</td>
                            <td style={td}></td>
                            <td style={td}></td>
                            <td style={td}>{formatCost(costs.totalCost)}</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section style={section}>
                <h2 style={h2}>Grading analysis</h2>
                {!analysis && <div style={{ color: '#6b7280' }}>No analysis document found in scanGrades.</div>}
                {analysis && (
                    <>
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
                            {overallGrade !== undefined && (
                                <KeyValue label="Overall grade" value={String(overallGrade)} />
                            )}
                            {analysis.isCorrect !== undefined && (
                                <KeyValue label="Verdict" value={analysis.isCorrect ? '✅ Correct' : '❌ Incorrect'} />
                            )}
                            {analysis.markschemeUsed !== undefined && (
                                <KeyValue label="Markscheme used" value={analysis.markschemeUsed ? 'Yes' : 'No'} />
                            )}
                            {analysis.rubricUsed !== undefined && (
                                <KeyValue label="Rubric used" value={analysis.rubricUsed ? 'Yes' : 'No'} />
                            )}
                        </div>

                        {status && (
                            <SubBlock title="Status">
                                <div style={feedbackBox}>{status}</div>
                            </SubBlock>
                        )}

                        {teacherAnalysis && (
                            <SubBlock title="Teacher analysis">
                                <AnalysisBody analysis={teacherAnalysis} />
                            </SubBlock>
                        )}

                        {studentAnalysis && (
                            <SubBlock title="Student analysis">
                                <AnalysisBody analysis={studentAnalysis} />
                            </SubBlock>
                        )}

                        {(defaultBreakdown || markschemeBreakdown) && (
                            <SubBlock title="Parameter breakdown">
                                <BreakdownTable breakdown={defaultBreakdown || markschemeBreakdown} />
                            </SubBlock>
                        )}

                        {conceptGrades.length > 0 && (
                            <SubBlock title="Concept grades">
                                <ConceptGradesTable rows={conceptGrades} />
                            </SubBlock>
                        )}

                        <details style={{ marginTop: 16 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 12, color: '#6b7280' }}>Raw analysis JSON</summary>
                            <pre style={pre}>{JSON.stringify(analysis, null, 2)}</pre>
                        </details>
                    </>
                )}
            </section>
        </div>
    );
};

const Row: React.FC<{ label: string; entry?: CostEntry }> = ({ label, entry }) => (
    <tr style={{ borderTop: '1px solid #e5e7eb' }}>
        <td style={td}>{label}</td>
        <td style={td}>{formatCost(entry?.inputCost)}</td>
        <td style={td}>{formatCost(entry?.outputCost)}</td>
        <td style={td}>{formatCost(entry?.totalCost)}</td>
    </tr>
);

const KeyValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div style={{ background: '#f9fafb', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
        <span style={{ color: '#6b7280', marginRight: 6 }}>{label}:</span>
        <strong>{value}</strong>
    </div>
);

const SubBlock: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
        {children}
    </div>
);

const renderList = (items: any): React.ReactNode => {
    if (!Array.isArray(items)) return <div style={feedbackBox}>{String(items)}</div>;
    return (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
            {items.map((item, idx) => (
                <li key={idx} style={{ marginBottom: 4 }}>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</li>
            ))}
        </ul>
    );
};

const AnalysisBody: React.FC<{ analysis: any }> = ({ analysis }) => {
    if (!analysis || typeof analysis !== 'object') {
        return <div style={feedbackBox}>{String(analysis)}</div>;
    }
    const entries = Object.entries(analysis);
    return (
        <div>
            {entries.map(([key, val]) => {
                if (key === 'conceptFeedback' && Array.isArray(val)) {
                    return (
                        <div key={key} style={{ marginBottom: 10 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>{humanize(key)}</div>
                            <ConceptFeedbackTable rows={val as any[]} />
                        </div>
                    );
                }
                return (
                    <div key={key} style={{ marginBottom: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>{humanize(key)}</div>
                        {Array.isArray(val) ? renderList(val)
                            : typeof val === 'string' ? <div style={feedbackBox}>{val}</div>
                            : typeof val === 'object' && val !== null ? <pre style={pre}>{JSON.stringify(val, null, 2)}</pre>
                            : <div style={feedbackBox}>{String(val)}</div>
                        }
                    </div>
                );
            })}
        </div>
    );
};

const ConceptFeedbackTable: React.FC<{ rows: any[] }> = ({ rows }) => (
    <table style={table}>
        <thead>
            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={th}>Concept ID</th>
                <th style={th}>Concept</th>
                <th style={th}>Feedback</th>
            </tr>
        </thead>
        <tbody>
            {rows.map((r, idx) => (
                <tr key={idx} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r?.conceptId || '—'}</td>
                    <td style={td}>{r?.conceptName || '—'}</td>
                    <td style={td}>{r?.feedback ?? <span style={{ color: '#9ca3af' }}>—</span>}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const BreakdownTable: React.FC<{ breakdown: any }> = ({ breakdown }) => {
    const params: any[] = Array.isArray(breakdown?.parameters) ? breakdown.parameters : [];
    if (params.length === 0) return <pre style={pre}>{JSON.stringify(breakdown, null, 2)}</pre>;
    return (
        <table style={table}>
            <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                    <th style={th}>Parameter</th>
                    <th style={th}>Score</th>
                    <th style={th}>Zone</th>
                    <th style={th}>Feedback</th>
                </tr>
            </thead>
            <tbody>
                {params.map((p, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={td}>{p?.name || '—'}</td>
                        <td style={td}>{p?.score ?? '—'}</td>
                        <td style={td}>{p?.zone || '—'}</td>
                        <td style={td}>{p?.feedback || '—'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const ConceptGradesTable: React.FC<{ rows: any[] }> = ({ rows }) => (
    <table style={table}>
        <thead>
            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={th}>Concept ID</th>
                <th style={th}>Concept</th>
                <th style={th}>Subject</th>
                <th style={th}>Score</th>
                <th style={th}>Grade</th>
            </tr>
        </thead>
        <tbody>
            {rows.map((r, idx) => (
                <tr key={idx} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r?.conceptId || '—'}</td>
                    <td style={td}>{r?.conceptName || '—'}</td>
                    <td style={td}>{r?.subject || '—'}</td>
                    <td style={td}>{r?.score ?? '—'}</td>
                    <td style={td}>{r?.grade ?? '—'}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const ImageThumb: React.FC<{ url: string; label: string }> = ({ url, label }) => {
    const [failed, setFailed] = useState(false);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <a href={url} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                {failed ? (
                    <div style={{
                        width: 260, height: 180,
                        border: '1px dashed #fca5a5', borderRadius: 6, background: '#fef2f2',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#b91c1c', fontSize: 12, padding: 12, textAlign: 'center',
                    }}>
                        Image failed to load (signed URL may be expired).<br />Click "Refresh images" above.
                    </div>
                ) : (
                    <img
                        src={url}
                        alt={label}
                        onError={() => setFailed(true)}
                        style={{ maxWidth: 260, maxHeight: 340, border: '1px solid #e5e7eb', borderRadius: 6, background: '#f9fafb' }}
                    />
                )}
            </a>
            <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#6b7280', textDecoration: 'none' }}>Open in new tab ↗</a>
        </div>
    );
};

const ImageGrid: React.FC<{ label: string; urls: string[] }> = ({ label, urls }) => {
    if (urls.length === 0) return null;
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 }}>{label} ({urls.length})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {urls.map((url, idx) => (
                    <ImageThumb key={`${url}-${idx}`} url={url} label={`${label} ${idx + 1}`} />
                ))}
            </div>
        </div>
    );
};

const humanize = (camelOrSnake: string): string => {
    const spaced = camelOrSnake
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const section: React.CSSProperties = { marginTop: 24, padding: 16, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' };
const h2: React.CSSProperties = { fontSize: 16, marginTop: 0, marginBottom: 12 };
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600, fontSize: 12, color: '#374151', borderBottom: '1px solid #e5e7eb' };
const td: React.CSSProperties = { padding: '10px' };
const pre: React.CSSProperties = { background: '#f3f4f6', padding: 12, borderRadius: 6, fontSize: 12, overflowX: 'auto', marginTop: 0, marginBottom: 0 };
const feedbackBox: React.CSSProperties = { background: '#f9fafb', padding: 10, borderRadius: 6, marginTop: 0, whiteSpace: 'pre-wrap', fontSize: 13 };

const refreshBtn = (disabled: boolean): React.CSSProperties => ({
    padding: '8px 14px',
    borderRadius: 6,
    border: '1px solid #2563eb',
    background: disabled ? '#93c5fd' : '#2563eb',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? 'wait' : 'pointer',
});

const refreshStatusBox = (tone: 'success' | 'error' | 'neutral'): React.CSSProperties => ({
    marginTop: 12,
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: 13,
    background: tone === 'success' ? '#ecfdf5' : tone === 'error' ? '#fef2f2' : '#f9fafb',
    color: tone === 'success' ? '#065f46' : tone === 'error' ? '#991b1b' : '#374151',
    border: `1px solid ${tone === 'success' ? '#a7f3d0' : tone === 'error' ? '#fecaca' : '#e5e7eb'}`,
});

export default ScanGradeQuestionPage;
